import type { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

export interface LayerFacade {
  bringForward(target?: fabric.Object): void;
  bringToFront(target?: fabric.Object): void;
  sendBackwards(target?: fabric.Object): void;
  sendToBack(target?: fabric.Object): void;
  canMoveUp(target?: fabric.Object): boolean;
  canMoveDown(target?: fabric.Object): boolean;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    layers?: LayerFacade;
  }
}

export interface LayerPluginOptions {}

const isGrid = (obj: fabric.Object): boolean => (obj.data as { name?: string } | undefined)?.name === 'grid';

export function layerPlugin(_options: LayerPluginOptions = {}): EditorPlugin {
  return {
    name: FabricPluginName.LAYER,
    install(editor: FabricEditor) {
      const canvas = editor.canvas;

      const resolveTargets = (target?: fabric.Object): fabric.Object[] => {
        const t = target ?? canvas.getActiveObject();
        if (!t) return [];
        const inner = (t as fabric.Object & { _objects?: fabric.Object[] })._objects;
        if (inner && inner.length > 0) return [...inner];
        return [t];
      };

      const pinGrid = () => {
        for (const obj of canvas.getObjects()) {
          if (isGrid(obj)) canvas.sendToBack(obj);
        }
      };

      const sortByZ = (objs: fabric.Object[], ascending: boolean): fabric.Object[] => {
        const all = canvas.getObjects();
        const indexed = objs.map((o) => ({ obj: o, idx: all.indexOf(o) }));
        indexed.sort((a, b) => (ascending ? a.idx - b.idx : b.idx - a.idx));
        return indexed.map((e) => e.obj);
      };

      const apply = (target: fabric.Object | undefined, op: (o: fabric.Object) => void, descending: boolean) => {
        const targets = resolveTargets(target).filter((o) => !isGrid(o));
        if (targets.length === 0) return;
        const ordered = sortByZ(targets, !descending);
        for (const o of ordered) op(o);
        pinGrid();
        canvas.requestRenderAll();
        editor.emit('object:modified', { target: targets[0] });
      };

      const facade: LayerFacade = {
        bringForward(target) {
          apply(target, (o) => canvas.bringForward(o), true);
        },
        bringToFront(target) {
          apply(target, (o) => canvas.bringToFront(o), true);
        },
        sendBackwards(target) {
          apply(target, (o) => canvas.sendBackwards(o), false);
        },
        sendToBack(target) {
          apply(target, (o) => canvas.sendToBack(o), false);
        },
        canMoveUp(target) {
          const targets = resolveTargets(target).filter((o) => !isGrid(o));
          if (targets.length === 0) return false;
          const all = canvas.getObjects();
          const topIndex = all.length - 1;
          const topmostTarget = Math.max(...targets.map((o) => all.indexOf(o)));
          return topmostTarget < topIndex;
        },
        canMoveDown(target) {
          const targets = resolveTargets(target).filter((o) => !isGrid(o));
          if (targets.length === 0) return false;
          const all = canvas.getObjects();
          const firstNonGrid = all.findIndex((o) => !isGrid(o));
          const bottomTarget = Math.min(...targets.map((o) => all.indexOf(o)));
          return bottomTarget > firstNonGrid;
        },
      };

      editor.layers = facade;
    },
    dispose(editor: FabricEditor) {
      editor.layers = undefined;
    },
  };
}
